import { ShopExtended, ShopBanner, ShopWidget, ShopPage, ShopPolicy, ShopGallery } from '../../domain/types';
import { COLLECTIONS } from '../../domain/collections';
import { db } from '../../infrastructure/firebaseConfig';
import { logger } from '../../infrastructure/logger';
import { cacheManager, CACHE_TTL } from '../../utils/cache';
import {
  collection,
  getDocs,
  getDoc,
  doc as firestoreDoc,
  query,
  where,
  orderBy,
  limit
} from "firebase/firestore";

export class ShopQueries {
  private shopsColl = COLLECTIONS.SHOPS;
  private bannersColl = 'shopBanners';
  private widgetsColl = 'shopWidgets';
  private pagesColl = 'shopPages';
  private policiesColl = 'shopPolicies';
  private galleriesColl = 'shopGalleries';

  async getShopById(id: string): Promise<ShopExtended | undefined> {
    try {
      const docRef = firestoreDoc(db, this.shopsColl, id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as ShopExtended;
      }
    } catch (error) {
      logger.error(`[ShopQueries] Error fetching shop ${id}`, error);
    }
    return undefined;
  }

  async getShopBySlug(slug: string): Promise<ShopExtended | undefined> {
    try {
      const q = query(
        collection(db, this.shopsColl),
        where("slug", "==", slug),
        where("status", "==", "active"),
        limit(1)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return { id: doc.id, ...doc.data() } as ShopExtended;
      }
    } catch (error) {
      logger.error(`[ShopQueries] Error fetching shop by slug ${slug}`, error);
    }
    return undefined;
  }

  async getShopByPartner(partnerId: string): Promise<ShopExtended | undefined> {
    try {
      const q = query(
        collection(db, this.shopsColl),
        where("partnerId", "==", partnerId),
        limit(1)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return { id: doc.id, ...doc.data() } as ShopExtended;
      }
    } catch (error) {
      logger.error(`[ShopQueries] Error fetching shop by partner ${partnerId}`, error);
    }
    return undefined;
  }

  async getActiveShops(limitCount: number = 50): Promise<ShopExtended[]> {
    try {
      const q = query(
        collection(db, this.shopsColl),
        where("status", "==", "active"),
        orderBy("stats.totalSales", "desc"),
        limit(limitCount)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ShopExtended));
    } catch (error) {
      logger.error('[ShopQueries] Error fetching active shops', error);
      return [];
    }
  }

  async getFeaturedShops(limitCount: number = 10): Promise<ShopExtended[]> {
    try {
      const now = new Date().toISOString();
      const q = query(
        collection(db, this.shopsColl),
        where("status", "==", "active"),
        where("featured", "==", true),
        where("featuredUntil", ">", now),
        orderBy("featuredUntil", "asc"),
        limit(limitCount)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ShopExtended));
    } catch (error) {
      logger.error('[ShopQueries] Error fetching featured shops', error);
      return [];
    }
  }

  async getShopsByCategory(category: string): Promise<ShopExtended[]> {
    try {
      const q = query(
        collection(db, this.shopsColl),
        where("categories", "array-contains", category),
        where("status", "==", "active"),
        orderBy("stats.rating", "desc")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ShopExtended));
    } catch (error) {
      logger.error(`[ShopQueries] Error fetching shops by category ${category}`, error);
      return [];
    }
  }

  async searchShops(searchQuery: string, limitCount: number = 20): Promise<ShopExtended[]> {
    try {
      const q = query(
        collection(db, this.shopsColl),
        where("status", "==", "active"),
        where("name", ">=", searchQuery),
        where("name", "<=", searchQuery + '\uf8ff'),
        limit(limitCount)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ShopExtended));
    } catch (error) {
      logger.error('[ShopQueries] Error searching shops', error);
      return [];
    }
  }

  async getShopsByIds(shopIds: string[]): Promise<ShopExtended[]> {
    if (shopIds.length === 0) return [];

    try {
      const cached: ShopExtended[] = [];
      const toFetch: string[] = [];

      for (const id of shopIds) {
        const shop = cacheManager.get<ShopExtended>('shop', id);
        if (shop) {
          cached.push(shop);
        } else {
          toFetch.push(id);
        }
      }

      if (toFetch.length === 0) return cached;

      const fetched = await Promise.all(
        toFetch.map(async (id) => {
          try {
            const shop = await this.getShopById(id);
            if (shop) {
              cacheManager.set('shop', id, shop, CACHE_TTL.SHOPS);
            }
            return shop;
          } catch (e) {
            logger.error(`[ShopQueries] Error fetching shop ${id}`, e);
            return undefined;
          }
        })
      );

      const validFetched = fetched.filter((s): s is ShopExtended => s !== undefined);
      return [...cached, ...validFetched];
    } catch (error) {
      logger.error('[ShopQueries] Error batch fetching shops', error);
      return [];
    }
  }

  async getPendingShops(): Promise<ShopExtended[]> {
    try {
      const q = query(
        collection(db, this.shopsColl),
        where("status", "==", "pending"),
        orderBy("createdAt", "asc")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ShopExtended));
    } catch (error) {
      logger.error('[ShopQueries] Error fetching pending shops', error);
      return [];
    }
  }

  async getCustomDomainShop(domain: string): Promise<ShopExtended | undefined> {
    try {
      const q = query(
        collection(db, this.shopsColl),
        where("settings.customDomain", "==", domain),
        where("status", "==", "active"),
        limit(1)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return { id: doc.id, ...doc.data() } as ShopExtended;
      }
    } catch (error) {
      logger.error(`[ShopQueries] Error fetching shop by custom domain ${domain}`, error);
    }
    return undefined;
  }

  async getBanners(shopId: string): Promise<ShopBanner[]> {
    try {
      const q = query(
        collection(db, this.bannersColl),
        where("shopId", "==", shopId),
        where("isActive", "==", true),
        orderBy("displayOrder", "asc")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ShopBanner));
    } catch (error) {
      logger.error(`[ShopQueries] Error fetching banners for shop ${shopId}`, error);
      return [];
    }
  }

  async getWidgets(shopId: string): Promise<ShopWidget[]> {
    try {
      const q = query(
        collection(db, this.widgetsColl),
        where("shopId", "==", shopId),
        where("isActive", "==", true),
        orderBy("displayOrder", "asc")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ShopWidget));
    } catch (error) {
      logger.error(`[ShopQueries] Error fetching widgets for shop ${shopId}`, error);
      return [];
    }
  }

  async getPages(shopId: string): Promise<ShopPage[]> {
    try {
      const q = query(
        collection(db, this.pagesColl),
        where("shopId", "==", shopId),
        where("isActive", "==", true),
        orderBy("displayOrder", "asc")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ShopPage));
    } catch (error) {
      logger.error(`[ShopQueries] Error fetching pages for shop ${shopId}`, error);
      return [];
    }
  }

  async getPageBySlug(shopId: string, slug: string): Promise<ShopPage | undefined> {
    try {
      const q = query(
        collection(db, this.pagesColl),
        where("shopId", "==", shopId),
        where("slug", "==", slug),
        limit(1)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return { id: doc.id, ...doc.data() } as ShopPage;
      }
    } catch (error) {
      logger.error('[ShopQueries] Error fetching page by slug', error);
    }
    return undefined;
  }

  async getPolicies(shopId: string): Promise<ShopPolicy[]> {
    try {
      const q = query(
        collection(db, this.policiesColl),
        where("shopId", "==", shopId),
        where("isActive", "==", true),
        orderBy("displayOrder", "asc")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ShopPolicy));
    } catch (error) {
      logger.error(`[ShopQueries] Error fetching policies for shop ${shopId}`, error);
      return [];
    }
  }

  async getGalleries(shopId: string): Promise<ShopGallery[]> {
    try {
      const q = query(
        collection(db, this.galleriesColl),
        where("shopId", "==", shopId),
        where("isActive", "==", true),
        orderBy("displayOrder", "asc")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ShopGallery));
    } catch (error) {
      logger.error(`[ShopQueries] Error fetching galleries for shop ${shopId}`, error);
      return [];
    }
  }
}

export const shopQueries = new ShopQueries();
